import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { errorMessage } from "../../shared/errors";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import type { AdminProductRow, ProductBranding } from "../../api/types";
import { Alert, Badge, Button, Card, Input, Spinner } from "../../ui";
import { StaffAction, stateTone } from "./shared";

/** What a product's sign-in and consent screens show; clients inherit it (docs/04 §9). */
function BrandingForm({
  product,
  disabled,
  onSaved,
}: {
  product: AdminProductRow;
  disabled: boolean;
  onSaved: () => void;
}) {
  const [webUrl, setWebUrl] = useState(product.web_url ?? "");
  const [draft, setDraft] = useState<ProductBranding>({
    name: product.branding?.name ?? product.name,
    logo_url: product.branding?.logo_url ?? "",
    primary_color: product.branding?.primary_color ?? "#302a7c",
    support_url: product.branding?.support_url ?? "",
  });
  const save = useMutation({
    mutationFn: () =>
      adminApi.updateProduct(product.id, {
        web_url: webUrl || null,
        branding: {
          name: draft.name || product.name,
          logo_url: draft.logo_url || null,
          primary_color: draft.primary_color || null,
          support_url: draft.support_url || null,
        },
      }),
    onSuccess: onSaved,
  });
  const field = (key: keyof ProductBranding) => ({
    value: draft[key] ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setDraft({ ...draft, [key]: e.target.value }),
    disabled,
  });
  return (
    <form
      className="mt-2 grid gap-2 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Input label="Display name" {...field("name")} />
      <Input
        label="Product URL"
        placeholder="https://tradebo.app"
        value={webUrl}
        disabled={disabled}
        onChange={(e) => setWebUrl(e.target.value)}
      />
      <Input
        label="Logo URL"
        placeholder="https://…/icon.png (square)"
        {...field("logo_url")}
      />
      <Input label="Primary colour" type="color" {...field("primary_color")} />
      <Input label="Support URL" {...field("support_url")} />
      <div className="flex items-center gap-3 sm:col-span-2">
        <Button type="submit" loading={save.isPending} disabled={disabled}>
          Save branding
        </Button>
        {draft.logo_url ? (
          <img src={draft.logo_url} alt="" className="h-8 w-8 rounded" />
        ) : (
          <span
            className="inline-block h-8 w-8 rounded"
            style={{ background: draft.primary_color ?? "#302a7c" }}
          />
        )}
        {save.isError ? (
          <span className="text-sm text-red-700">
            {errorMessage(save.error)}
          </span>
        ) : null}
        {save.isSuccess ? (
          <span className="text-sm text-slate-600">Saved.</span>
        ) : null}
      </div>
    </form>
  );
}

/** Products and clients (owner). Creation and price changes stay in the CLI / seed. */
export function ProductsPage() {
  const { role } = useOutletContext<{ role: StaffRole }>();
  const isOwner = roleAtLeast(role, "owner");
  const products = useQuery({
    queryKey: ["admin", "products"],
    queryFn: adminApi.products,
  });
  const clients = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => adminApi.clients({}),
  });
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const show = (
    fn: () => Promise<{ webhook_secret?: string; client_secret?: string }>,
  ) =>
    fn()
      .then((r) => {
        setSecret(r.webhook_secret ?? r.client_secret ?? null);
        setMessage(null);
      })
      .catch((err) => setMessage(errorMessage(err)));
  return (
    <div className="flex flex-col gap-4">
      {message ? <Alert kind="error">{message}</Alert> : null}
      {secret ? (
        <Alert kind="success">
          New secret (shown once): <code>{secret}</code>
        </Alert>
      ) : null}
      <Card>
        <h1 className="mb-2 text-xl font-semibold">Products</h1>
        {products.isLoading ? <Spinner /> : null}
        <ul className="text-sm">
          {(products.data ?? []).map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 py-1">
              <span className="font-medium">{p.slug}</span> {p.name}{" "}
              <Badge tone={stateTone(p.status)}>{p.status}</Badge>
              <span className="text-xs text-slate-500">
                {p.webhook_url ?? "no webhook"}
              </span>
              <span className="ml-auto">
                <StaffAction
                  allowed={isOwner}
                  title="Owner role required"
                  onClick={() =>
                    void show(() => adminApi.rotateWebhookSecret(p.id))
                  }
                >
                  Rotate webhook secret
                </StaffAction>
              </span>
              <div className="w-full">
                <BrandingForm
                  product={p}
                  disabled={!isOwner}
                  onSaved={() =>
                    void queryClient.invalidateQueries({
                      queryKey: ["admin", "products"],
                    })
                  }
                />
              </div>
            </li>
          ))}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-semibold">Clients</h2>
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-1">Client</th>
              <th className="px-3 py-1">Kind</th>
              <th className="px-3 py-1">Product</th>
              <th className="px-3 py-1">Status</th>
              <th className="px-3 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {(clients.data ?? []).map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="px-3 py-1">
                  {c.client_id}
                  <span className="block text-xs text-slate-500">{c.name}</span>
                </td>
                <td className="px-3 py-1">{c.kind}</td>
                <td className="px-3 py-1">{c.product ?? "—"}</td>
                <td className="px-3 py-1">
                  <Badge tone={stateTone(c.status)}>{c.status}</Badge>
                </td>
                <td className="flex justify-end gap-2 px-3 py-1">
                  {c.kind === "product" || c.kind === "service" ? (
                    <StaffAction
                      allowed={isOwner}
                      title="Owner role required"
                      onClick={() =>
                        void show(() =>
                          adminApi.rotateClientSecret(c.client_id),
                        )
                      }
                    >
                      Rotate secret
                    </StaffAction>
                  ) : null}
                  {c.status === "active" ? (
                    <StaffAction
                      allowed={isOwner}
                      variant="danger"
                      title="Owner role required"
                      onClick={() =>
                        adminApi
                          .disableClient(c.client_id)
                          .then(() => clients.refetch())
                          .catch((err) => setMessage(errorMessage(err)))
                      }
                    >
                      Disable
                    </StaffAction>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
